/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */


import { ProgressIndicator } from '@/components/blocks/ProgressIndicator';
import { storeLocatorMapContainerSX } from '@/components/content/StoreLocator/styles/mapContainer';
import { STATIC_HOST_SECURE } from '@/data/config/STATIC_HOST_SECURE';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useStoreLocator } from '@/data/Content/StoreLocator';
import { useStoreLocatorCityPage } from '@/data/Content/StoreLocatorCityPage';
import { ContentProvider } from '@/data/context/content';
import { useDeviceType } from '@/data/deviceDetection';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { useGoogleMapsAPILocale } from '@/data/state/useGoogleMapsAPILocale';
import { ID } from '@/data/types/Basic';
import { LatLng } from '@/data/types/Store';
import { findStoreAttributeByNameStartsWith } from "@/data/utils/StoreStock";
import { Box, Grid, List, ListItem, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { GoogleMap, MarkerClusterer } from '@react-google-maps/api';
import { usePathname } from 'next/navigation';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ContentRecommendation } from '../ContentRecommendation';
import { StoreLocatorAutocomplete } from '../StoreLocator/parts/Autocomplete';
import { StoreLocatorInfoBox } from '../StoreLocator/parts/InfoBox';
import { StoreLocatorMarker } from '../StoreLocator/parts/Marker';
import { storeLocatorSideListElementSX } from '../StoreLocator/styles/SideList/element';
import { googleMapSx, storeLocatorGridContainerSX } from '../StoreLocator/styles/SideList/gridContainer';
import { storeLocatorSideListSideListSX } from '../StoreLocator/styles/SideList/sideList';
import { storeLocatorSideListTitleTypographySX } from '../StoreLocator/styles/SideList/titleTypography';
import { StoreDetailsDisplay } from './parts/StoreDetailsDisplay';
import { cityPageBackToWinkelSX } from './styles/storeCityStyles';
import { MARKER_CLUSTER_STYLES } from '@/utils/getClusterStyles';

export const StoreLocatorCity: FC<{ id: ID }> = ({ id }) => {
	const router = useNextRouter();
	const routes = useLocalization('Routes');
	const { loadedLocale } = useGoogleMapsAPILocale();
	const { locale } = router;
	const {settings} = useSettings();
	const {isDesktop} = useDeviceType();

	const staticHostSecure = useMemo(()=>settings?.userData?.StaticHostSecure??STATIC_HOST_SECURE,[settings])
	    
	const [mapCenter, setMapCenter] = useState<LatLng>({} as LatLng);
	const [zoom, setZoom] = useState<number>();
	const localization = useLocalization('StoreLocator');
	const path = usePathname();
	const cityName = useMemo(()=> path?.split("/")?.at(-1)?.replace('speelgoedwinkel-','')?.trim()??"",[path]);

	const {
		locator,
		showDirection,
		destination,
		directionResults,
		searchTextFieldRef,
		currentLocation,
		clickedIndex,
		searchTerm,
		clearSearch,
		mapOnLoad,
		directionsCallback,
		findNearStore,
		onPlaceChanged,
		searchBoxOnLoad,
		onListItemClick,
		...rest
	} = useStoreLocator();
	const {stores:cityStores, isRedirect, cityName:cityNameDB} = useStoreLocatorCityPage(cityName);

	useEffect(()=>{
		if(isRedirect){
			router.push(routes.StoreLocator.route.t());
		}
	},[isRedirect])

	useEffect(()=>{
		if(loadedLocale && cityStores?.length>0){
			setMapCenter(cityStores[0].coordinates)
			setZoom(11);
		}
	},[cityStores, loadedLocale]);
	const today = new Date(); 
	const todayDay = today.toLocaleDateString("nl-NL", { weekday: 'long' }); 
	const formatter = new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
	const formattedDate = formatter.format(today);

	const storeHours = useMemo(()=>{
		
		let tempStoreHrs:Record<string,string> = {};
		cityStores.forEach((store) => {
			const specialTimes = findStoreAttributeByNameStartsWith(store?.attributes,'specialopen');
			const specialHoursToday = specialTimes?.find((spDate)=>spDate.displayName?.includes(formattedDate));
			const todayTimes = store?.attributes?.find((attr)=> (attr.displayName?.toLowerCase() == todayDay?.toLowerCase() && attr.name.startsWith('open')));
			const storeClosedToday = (specialHoursToday?.displayValue?.toLowerCase() === 'gesloten'.toLowerCase()) &&
			(todayTimes?.displayValue?.toLowerCase() === 'gesloten'.toLowerCase());
			if (storeClosedToday){
				tempStoreHrs[store.physicalStoreName]='Gesloten';
			}else if (specialHoursToday){
				tempStoreHrs[store.physicalStoreName]=specialHoursToday.displayValue.replace('-',' tot ');
			} else if (todayTimes){
				tempStoreHrs[store.physicalStoreName]=todayTimes.displayValue.replace('-',' tot ');
			}
			});
			
	return tempStoreHrs;
	},[cityStores])
 
	const goToStoreInMap = (latLon: LatLng) => {
		setMapCenter(latLon);
		setZoom(16);
	}
	
	const onBackToStloc = useCallback(() => {
		router.push(routes.StoreLocator.route.t());
	}, [
	]);
	
	return(
			<ContentProvider
				value={{
					clickedIndex,
					searchTerm,
					searchTextFieldRef,
					currentLocation,
					findNearStore,
					searchBoxOnLoad,
					onPlaceChanged,
					onListItemClick,
					clearSearch,
					goToStoreInMap,
					stores:cityStores,
					storeHours,
					staticHostSecure,
					setMapCenter,
					setZoom,
					...rest,
				}}
			>
				<Typography onClick={onBackToStloc} sx={cityPageBackToWinkelSX}>{localization.storeStreetBackLink.t()} </Typography>
				<Grid container>
					<Grid item sx={{ color: '#353535', paddingBottom: '26px' }}>
						<ContentRecommendation
							id={`${cityNameDB}_CS_Header_Espot`}
							properties={{ emsName: `${cityNameDB}_CS_Header_Espot`}}
						/>
					</Grid>
				</Grid>
				<Grid
					container
					justifyContent="center"
					alignItems="stretch"
					spacing={2}
					paddingBottom={4}
					sx={storeLocatorGridContainerSX}
					flexDirection={'row'}
				>
					{
						<Grid item xs md={4}>
							<Paper sx={{ borderRadius: 0, padding: 1 , height:'100%'}}>
								<Grid container justifyContent="space-between" alignItems="flex-end" spacing={1}>
									<Grid item flex={1}>
										<ListItemText sx={storeLocatorSideListElementSX}
											primary={
												<Typography variant="subtitle2" sx={storeLocatorSideListTitleTypographySX}>
													{localization.searchStlocStore.t()}
												</Typography>
											}
										/>
									</Grid>
								</Grid>
								<StoreLocatorAutocomplete />
								{isDesktop &&
									<Stack sx={storeLocatorSideListSideListSX}>
										<List sx={{ maxHeight: '600px' }} disablePadding >
											<ListItem>
												<Box pb={2} pt={0}>
													<StoreDetailsDisplay />
												</Box>
											</ListItem>
										</List>
									</Stack>
									}
								</Paper>
							</Grid>
						}
						{loadedLocale && locale === loadedLocale.locale ?
							<Grid item xs>
								<Grid sx={googleMapSx}>
									<GoogleMap
										mapContainerStyle={{ height: '100vh', width: '100%', maxHeight:620, minHeight: 720 }}
										zoom={zoom}
										center={mapCenter}
										onLoad={mapOnLoad}
									>
										<MarkerClusterer
											options={{
												styles: MARKER_CLUSTER_STYLES,
											}}
											children={(clusterer)=>
												<>
													{cityStores?.map((store, index)=>(
														<StoreLocatorMarker key={store.id} store={store} index={index} clusterer={clusterer} />
													))}
												</>
											}
										/>
									</GoogleMap>
								</Grid>
								<Grid item>
									<StoreLocatorInfoBox />
								</Grid>
							</Grid>
							:
							<Grid item xs md={8} minHeight='80vh'><ProgressIndicator /></Grid>
						}
					{!isDesktop &&
						<Grid item xs md={4} sx={isDesktop ? storeLocatorMapContainerSX : null}>
							<Paper sx={{ borderRadius: 0 }}>
								<Stack sx={storeLocatorSideListSideListSX}>
									<List sx={{ maxHeight: '600px' }} disablePadding >
										<ListItem>
											<Box pb={2} pt={0}>
												<StoreDetailsDisplay />
											</Box>
										</ListItem>
									</List>
								</Stack>
							</Paper>
						</Grid>
					}
				</Grid>
				<Grid>
					<ContentRecommendation
						id={`${cityNameDB}_CS_Footer_Espot`}
						properties={{ emsName: `${cityNameDB}_CS_Footer_Espot`}}
					/>
				</Grid>
		</ContentProvider>
	);
};
